import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import NotFound from './NotFound.jsx'

/**
 * `/legal/terms` and `/legal/privacy` (PLAN §17.5, §24).
 *
 * Plain-language drafts, written to be read rather than to be defended. PLAN
 * §26 item 5 defers the real legal review to the owner, so nothing here claims
 * to be reviewed — the page says so itself, in the alpha note at the top,
 * because a policy page that implies a lawyer saw it when none did is worse
 * than no page.
 *
 * Two things on this page are load-bearing rather than boilerplate, and both
 * are §24 checklist items: the MusicBrainz and Cover Art Archive attributions,
 * and the statement that Sonologue makes no Spotify API calls.
 */
const UPDATED = '2 August 2026'

function Terms() {
  return (
    <>
      <h1 className="large-title">Terms of use</h1>
      <p className="page-intro">Draft for the closed alpha · last changed {UPDATED}</p>

      <h2 className="title-3">What this is</h2>
      <p>
        Sonologue is a place to rate and review music and to see what the people you follow are
        listening to. It is in a closed alpha: it is invite-only, it will change under you, and it
        may lose data in ways a finished product would not. Do not treat it as the only copy of
        anything you care about — <Link to="/settings">Settings</Link> has a button that gives you
        everything you have written, at any time and for nothing.
      </p>

      <h2 className="title-3">Your account</h2>
      <ul>
        <li>You need an invite, and one account per person.</li>
        <li>
          You are responsible for what you post. Keep your password to yourself; tell the operator
          if you think somebody else has it.
        </li>
        <li>
          You can leave whenever you like. Deleting your account signs you out everywhere at once
          and erases it after fourteen days, and you choose whether what you wrote goes with it.
        </li>
      </ul>

      <h2 className="title-3">What you write</h2>
      <p>
        Your reviews, lists and comments stay yours. By posting them you let Sonologue show them to
        other people here — which is the whole point of posting — and nothing more: they are not
        sold, licensed on, or used to train anything.
      </p>
      <p>
        Do not post things that are illegal, that harass somebody, or that you do not have the
        right to post. One person moderates this alpha. Reports go to them, and they can hide
        anything on the site. Hiding is not deleting: the author still sees their own copy.
      </p>

      <h2 className="title-3">Music data</h2>
      <p>
        Album, artist and song information comes from{' '}
        <a href="https://musicbrainz.org" rel="noreferrer noopener" target="_blank">
          MusicBrainz
        </a>
        , whose core data is in the public domain (CC0). Cover art comes from the{' '}
        <a href="https://coverartarchive.org" rel="noreferrer noopener" target="_blank">
          Cover Art Archive
        </a>
        ; those images belong to their copyright holders and are shown here for identification.
        Links to Spotify, Apple Music and YouTube Music are ordinary search links — Sonologue holds
        no account of yours on any of them and calls none of their APIs.
      </p>

      <h2 className="title-3">No promises</h2>
      <p>
        This is a side project running on one machine. It is provided as it is, with no warranty
        and no guarantee that it will be up, be fast, or keep working. The operator can end the
        alpha, or your access to it, at any time — and will tell you first, and give you your data.
      </p>

      <h2 className="title-3">Changes</h2>
      <p>
        These terms will change as the product does. Material changes will be announced in the app
        before they take effect.
      </p>
    </>
  )
}

function Privacy() {
  return (
    <>
      <h1 className="large-title">Privacy</h1>
      <p className="page-intro">Draft for the closed alpha · last changed {UPDATED}</p>

      <h2 className="title-3">What Sonologue keeps</h2>
      <ul>
        <li>
          <strong>Your account</strong> — email address, username, display name, bio, and a hash of
          your password. The password itself is never stored.
        </li>
        <li>
          <strong>What you do here</strong> — ratings and their history, reviews, lists, comments,
          hearts, follows, blocks and mutes.
        </li>
        <li>
          <strong>A security log</strong> — sign-ins, password changes and moderation actions, with
          a shortened form of your IP address. Not the whole address.
        </li>
      </ul>

      <h2 className="title-3">What it does not</h2>
      <p>
        There are no third-party analytics, no advertising, and no tracking pixels — not in the
        alpha and not later. Nothing you do here is sent to another company. Sonologue does not
        know what you listen to unless you tell it by rating something.
      </p>

      <h2 className="title-3">Who can see what</h2>
      <p>
        Reviews, public lists and public ratings are visible to anybody signed in. A private
        account is visible only to followers it has approved. A private rating counts towards
        nothing anybody else sees. Blocking somebody removes each of you from the other&rsquo;s
        pages, searches, comment threads and rating histograms, in both directions.
      </p>

      <h2 className="title-3">Getting your data out</h2>
      <p>
        <Link to="/settings">Settings → Your data</Link> builds a zip of everything above — as JSON
        for a program and CSV for a spreadsheet — and emails you a link. It is free, there is no
        limit worth mentioning, and it works while your account is on its way out.
      </p>

      <h2 className="title-3">Deleting it</h2>
      <p>
        Deleting your account signs you out everywhere immediately and hides it from everybody.
        Fourteen days later it is purged: your email address, password, bio and name are gone, your
        ratings are deleted and leave every community average, your hearts and notifications go,
        and every follow and block involving you is cut. You choose at the time whether your
        published reviews, comments and lists are deleted too or stay where they are credited to
        &ldquo;Deleted account&rdquo;. The fourteen days are undoable from a link in the email;
        after them, nothing is.
      </p>

      <h2 className="title-3">Email</h2>
      <p>
        Sonologue sends verification links, password resets, invites and the two messages above. It
        sends nothing else — no newsletter, no digest, no re-engagement mail.
      </p>

      <h2 className="title-3">Asking</h2>
      <p>
        One person runs this. Anything you want to know about your data, ask them; the answer will
        not be a form.
      </p>
    </>
  )
}

export default function Legal() {
  const { page } = useParams()
  const isPrivacy = page === 'privacy'
  // Only these two have ever had content. Everything else used to fall
  // through to the Terms, which served a document nobody asked for and said
  // nothing about the address being wrong.
  const known = isPrivacy || page === 'terms'

  useEffect(() => {
    if (!known) return
    document.title = `${isPrivacy ? 'Privacy' : 'Terms'} — Sonologue`
  }, [isPrivacy, known])

  if (!known) return <NotFound />

  return (
    <article className="card card-pad prose legal">
      <p className="alpha-note">
        A plain-language draft for the closed alpha. It has not been through a legal review, and it
        says what Sonologue actually does today rather than what a lawyer would have it promise.
      </p>
      {isPrivacy ? <Privacy /> : <Terms />}
      <p className="footnote mt-6">
        {isPrivacy ? (
          <Link to="/legal/terms">Terms of use</Link>
        ) : (
          <Link to="/legal/privacy">Privacy</Link>
        )}
      </p>
    </article>
  )
}
